import React, { useState, useEffect, useRef } from 'react';
import { useSocket } from '../../hooks/useSocket';
import { apiClient } from '../../services/api';

interface DocumentEditorProps {
  document?: {
    id: string;
    name: string;
    content: any;
    version: number;
  } | null;
  roomId?: string;
  onClose: () => void;
  onSave: (content: string) => void;
}

interface DocumentChange {
  userId: string;
  username: string;
  change: {
    type: 'insert' | 'delete';
    position: number;
    text?: string;
    length?: number;
  };
  timestamp: string;
}

const DocumentEditor: React.FC<DocumentEditorProps> = ({
  document,
  roomId,
  onClose,
  onSave
}) => {
  const { socket } = useSocket();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  // Initialize document content
  useEffect(() => {
    if (document) {
      setTitle(document.name);
      setContent(typeof document.content === 'string' ? document.content : JSON.stringify(document.content, null, 2));
    } else {
      setTitle('');
      setContent('');
    }
  }, [document]);

  // Socket events for collaborative editing
  useEffect(() => {
    if (!socket || !document) return;

    const handleDocumentChange = (data: DocumentChange) => {
      // Apply remote changes to local content
      const { change } = data;
      
      setContent(prevContent => {
        if (change.type === 'insert' && change.text) {
          return prevContent.slice(0, change.position) + change.text + prevContent.slice(change.position);
        } else if (change.type === 'delete' && change.length) {
          return prevContent.slice(0, change.position) + prevContent.slice(change.position + change.length);
        }
        return prevContent;
      });
    };

    const handleCollaboratorJoined = (data: { userId: string; username: string }) => {
      setCollaborators(prev => [...prev.filter(c => c !== data.username), data.username]);
    };

    const handleCollaboratorLeft = (data: { userId: string; username: string }) => {
      setCollaborators(prev => prev.filter(c => c !== data.username));
    };

    const handleDocumentLocked = (data: { lockedBy: string }) => {
      console.log(`Document locked by ${data.lockedBy}`);
    };

    // Join document editing session
    socket.emit('document:join', { documentId: document.id, roomId });

    socket.on('document:change', handleDocumentChange);
    socket.on('document:collaboratorJoined', handleCollaboratorJoined);
    socket.on('document:collaboratorLeft', handleCollaboratorLeft);
    socket.on('document:locked', handleDocumentLocked);

    return () => {
      socket.emit('document:leave', { documentId: document.id });
      socket.off('document:change', handleDocumentChange);
      socket.off('document:collaboratorJoined', handleCollaboratorJoined);
      socket.off('document:collaboratorLeft', handleCollaboratorLeft);
      socket.off('document:locked', handleDocumentLocked);
    };
  }, [socket, document, roomId]);

  // Auto-save functionality
  useEffect(() => {
    if (!document || !content) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [content, title]);

  const handleContentChange = (newContent: string) => {
    const oldContent = content;
    setContent(newContent);

    // Emit real-time changes
    if (socket && document && newContent !== oldContent) {
      const change = calculateChange(oldContent, newContent);
      if (change) {
        socket.emit('document:change', {
          documentId: document.id,
          roomId,
          change
        });
      }
    }

    setIsTyping(true);
    setTimeout(() => setIsTyping(false), 1000);
  };

  const calculateChange = (oldText: string, newText: string) => {
    // Simple diff algorithm - find first difference
    let i = 0;
    while (i < Math.min(oldText.length, newText.length) && oldText[i] === newText[i]) {
      i++;
    }

    if (newText.length > oldText.length) {
      // Insertion
      return {
        type: 'insert' as const,
        position: i,
        text: newText.slice(i, i + (newText.length - oldText.length))
      };
    } else if (newText.length < oldText.length) {
      // Deletion
      return {
        type: 'delete' as const,
        position: i,
        length: oldText.length - newText.length
      };
    }

    return null;
  };

  const handleSave = async () => {
    if (!document && !title.trim()) return;

    setIsSaving(true);
    try {
      if (document) {
        // Update existing document
        await apiClient.updateDocument(document.id, {
          name: title,
          content: content
        });
      } else {
        // Create new document
        const newDoc = await apiClient.createDocument({
          name: title || 'Untitled Document',
          content: content,
          roomId: roomId || null,
          type: 'text'
        });
        console.log('Document created:', newDoc);
      }

      setLastSaved(new Date());
      onSave(content);
    } catch (error) {
      console.error('Error saving document:', error);
      alert('Failed to save document. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    handleSave();
  };

  if (!document && !title && !content) {
    return (
      <div className="card h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📝</div>
          <div className="text-white/70 mb-4">Select or create a document to edit</div>
          <button 
            onClick={() => setTitle('New Document')}
            className="bg-accent text-primary px-6 py-3 rounded-lg hover:bg-accent/90 font-medium"
          >
            Create New Document
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card h-96 flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex-1">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Document title..."
            className="text-lg font-bold bg-transparent border-none outline-none text-white placeholder-white/50 w-full"
          />
          <div className="text-xs text-white/50 mt-1">
            {document ? `Version ${document.version}` : 'New document'} • 
            {isSaving ? ' Saving...' : lastSaved ? ` Last saved ${lastSaved.toLocaleTimeString()}` : ' Not saved'}
            {collaborators.length > 0 && ` • ${collaborators.length} other${collaborators.length !== 1 ? 's' : ''} editing`}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleManualSave}
            disabled={isSaving}
            className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded hover:bg-blue-500/30 text-sm disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Collaborators */}
      {collaborators.length > 0 && (
        <div className="mb-2">
          <div className="flex items-center gap-2 text-xs text-white/60">
            <span>👥 Active collaborators:</span>
            {collaborators.map(name => (
              <span key={name} className="px-2 py-1 bg-green-500/20 text-green-400 rounded">
                {name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1 bg-white/5 rounded-lg overflow-hidden">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => handleContentChange(e.target.value)}
          placeholder="Start writing your document..."
          className="w-full h-full p-4 bg-transparent border-none outline-none text-white placeholder-white/50 resize-none font-mono text-sm leading-relaxed"
        />
      </div>

      {/* Status bar */}
      <div className="flex justify-between items-center mt-2 text-xs text-white/50">
        <div>
          {content.length} characters • {content.split('\n').length} lines
        </div>
        <div className="flex items-center gap-2">
          {isTyping && (
            <span className="text-blue-400">
              ✏️ Typing...
            </span>
          )}
          <span className={`w-2 h-2 rounded-full ${
            isSaving ? 'bg-yellow-400 animate-pulse' : 
            lastSaved ? 'bg-green-400' : 'bg-red-400'
          }`}></span>
        </div>
      </div>
    </div>
  );
};

export default DocumentEditor;